import Cookies from "universal-cookie";
import { EDefaultConfigValues, TrustedWidgetConfig } from "../types";

const cookies = new Cookies();

// Константы
export const REFRESH_TOKEN_TTL = 30 * 24 * 60 * 60; // 30 дней в секундах

// Проверка валидности JWT токена
export const isTokenValid = async (token: string): Promise<boolean> => {
  if (!token) return false;

  try {
    // Простая проверка структуры JWT токена
    const parts = token.split(".");
    if (parts.length !== 3) return false;

    // Декодируем payload для проверки срока действия
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    const now = Math.floor(Date.now() / 1000);

    return payload.exp && payload.exp > now;
  } catch (e) {
    console.log("isTokenValid error:", e);
    return false;
  }
};

// Удаление токенов из cookies
export const removeTokenCookies = (): void => {
  cookies.remove("accessToken", { path: "/" });
  cookies.remove("refreshToken", { path: "/" });
};

// Установка токенов в cookies
export const setTokenCookies = (
  accessToken: string,
  refreshToken: string,
  expiresIn: number
): void => {
  if (accessToken && refreshToken) {
    removeTokenCookies();
  }

  const accessTokenExpiration = new Date();
  accessTokenExpiration.setSeconds(
    accessTokenExpiration.getSeconds() + expiresIn
  );

  if (accessToken) {
    cookies.set("accessToken", accessToken, {
      expires: accessTokenExpiration,
    });
  }

  const refreshTokenExpiration = new Date();
  refreshTokenExpiration.setSeconds(
    refreshTokenExpiration.getSeconds() + REFRESH_TOKEN_TTL
  );

  if (refreshToken) {
    cookies.set("refreshToken", refreshToken, {
      expires: refreshTokenExpiration,
    });
  }
};

// Декодирование JWT токена
export const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (e) {
    console.log("decodeJWT error: " + e);
  }
};

// Получение криптографического объекта
export const getCrypto = (): Crypto => {
  return window.crypto;
};

// Генерация случайной строки
export const randomString = (): string => {
  const charset =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  let str = "";
  const randomValues = Array.from(
    getCrypto().getRandomValues(new Uint8Array(43))
  );
  randomValues.forEach((v) => (str += charset[v % charset.length]));
  return str;
};

// Установка code verifier в localStorage
export const setCodeVerifier = (): string => {
  const codeVerifier = randomString();
  window?.localStorage.setItem("codeVerifier", codeVerifier);
  return codeVerifier;
};

// Конвертация ArrayBuffer в base64url
export const btoaRFC7636 = (buf: ArrayBuffer): string => {
  let binary = "";
  const bytes = new Uint8Array(buf);
  const length = bytes.byteLength;
  for (let i = 0; i < length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
};

// Конвертация строки в ArrayBuffer
export const stringToArrayBuffer = (str: string): ArrayBuffer => {
  const encoder = new TextEncoder();
  return encoder.encode(str);
};

// SHA256 хеширование
export const sha256 = async (str: string): Promise<ArrayBuffer> => {
  const buffer = stringToArrayBuffer(str);
  const hashBuffer = await crypto.subtle?.digest("SHA-256", buffer);
  return hashBuffer;
};

// Получение URL параметров
export const getUrlParams = (): string => {
  const queryString = window?.location.search;
  const urlParams = new URLSearchParams(queryString);
  const code = urlParams.get("code");

  return code || "";
};

// Обмен кода на токены
export const getTokensByCode = async (
  config: TrustedWidgetConfig
): Promise<{
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}> => {
  const code = getUrlParams();
  const codeVerifier = window?.localStorage.getItem("codeVerifier");
  const codeVerifierBase64 = codeVerifier
    ? btoaRFC7636(stringToArrayBuffer(codeVerifier))
    : "";

  let accessToken = "";
  let idToken = "";
  let refreshToken = "";
  let expiresIn;

  try {
    const body = new URLSearchParams({
      code,
      code_verifier: codeVerifierBase64,
      redirect_uri: config.redirectUrl || window?.location.href,
      grant_type: "authorization_code",
      client_id: config.appId,
    });

    const response = await fetch(
      `${
        config.backendUrl +
        (config.getTokenEndPoint || EDefaultConfigValues.getTokenEndPoint)
      }`,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        method: "POST",
        body,
      }
    );

    if (response.ok) {
      const tokenData = await response.json();
      accessToken = tokenData.access_token || "";
      idToken = tokenData.id_token || "";
      refreshToken = tokenData.refresh_token || "";
      expiresIn = tokenData.expires_in;
    } else {
      const errorText = await response.text();
      console.error("Token request failed:", response.status, errorText);
    }
  } catch (e) {
    console.log("getTokenByCode error: ", e);
  }

  return { accessToken, idToken, refreshToken, expiresIn };
};

// Обработка логина через OIDC
export const login = async (config: TrustedWidgetConfig) => {
  const codeVerifier = setCodeVerifier();
  const codeVerifierBase64 = btoaRFC7636(stringToArrayBuffer(codeVerifier));
  const codeChallenge = btoaRFC7636(await sha256(codeVerifierBase64));

  const params = new URLSearchParams({
    client_id: config.appId,
    response_type: "code",
    scope: config.scopes?.join(" ") + " " + EDefaultConfigValues.scopes,
    redirect_uri: config.redirectUrl || window.location.href,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    prompt: "consent",
  });

  const url = `${
    config.issuer || EDefaultConfigValues.issuer
  }/api/oidc/auth?${params.toString()}`;
  window.location.href = url;
};

export const logout = (): void => {
  try {
    removeTokenCookies();

    // Удаляем все куки связанные с аутентификацией
    const allCookies = cookies.getAll();
    Object.keys(allCookies).forEach((cookieName) => {
      if (
        cookieName.includes("token") ||
        cookieName.includes("auth") ||
        cookieName.includes("session")
      ) {
        cookies.remove(cookieName, { path: "/" });
      }
    });

    window?.localStorage.clear();
    window?.sessionStorage.clear();

    window.location.href = "/";
  } catch (error) {
    console.error("Ошибка при агрессивном выходе:", error);
    window?.location.reload();
  }
};
